import {
  HttpEvent,
  HttpRequest,
  HttpResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { from, lastValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthService } from "src/app/services/auth-service/auth.service";
import { NetworkService } from "src/app/services/network-service/network.service";
import { StorageService } from "src/app/services/storage-service/storage.service";
import { SKIP_AUTH_INTERCEPTOR } from "src/app/services/http-service/http-client.service";
const OFFLINE_ENDPOINTS = [
  "closeFinding",
  "assesmentCreation",
  "addFindingToiCare",
  "addFindingForMaterialManagement",
  "updateAssessmentStatus",
  "updateMaterialAssessmentStatus",
  "noNewFinding",
];

const NO_HEADER_ENDPOINTS = [
  "configure-site",
  "deleteQrBogPro",
  "getAllActiveGru",
  "getRegionBogPro",
  "getAllBogRegion",
  "getRandRDataBogPro",
  "getSiteObservationCountsBogPro",
  "createAnUserBogPro",
  "syncIcareLocations",
  "getSingleUserBogPro",
  "actionClosureBogPro",
  "testIcareConnection",
  "reAssignActionBogPro",
  "saveOrUpdateQrBogPro",
  "getWhosWhoDataBogPro",
  "getTODOActionsBogPro",
  "syncIcareDepartments",
  "getAllActiveGruBogPro",
  "downloadReportsBogPro",
  "getAllBogRegionGroups",
  "updateLevelInfoBogPro",
  "getIcareLocationBogPro",
  "getLocalCurrencyBogPro",
  "configure-site-fencing",
  "sendReportToMailBogPro",
  "getAllDepartmentBogPro",
  "getBogSingleUserBogPro",
  "updateSingleUserBogPro",
  "getChildDataByIdBogPro",
  "getSegmentsMasterBogPro",
  "getQRFromSiteByIdBogPro",
  "configure-site-zoneName",
  "getAllUnitsWithinLevels",
  "getAllUserProfileBogPro",
  "getReportTemplateBogPro",
  "transferLevelDataBogPro",
  "createHighLowLightBogPro",
  "getAllPlantsWithinLevels",
  "saveReportTemplateBogPro",
  "addSinglePolishedIncident",
  "getLocationHirerchyConfig",
  "addCriticalIncidentPolished",
  "updateLocationHirerchyConfig",
  "addLocationMasterLevelBogPro",
  "syncIcareSecondaryWorkgroups",
  "getHousekeepingStatsForGRUBogPro",
  "getHousekeepingCommentsForSiteBogPro",
  "getCountryFromRegionCollectionBogPro",
  "getAllOpcoFromRegionCollectionBogPro",
  "getOpcoByCountryFromRegionCollectionBogPro",
];

const PUBLIC_ENDPOINTS = [
  "getOtp",
  "verifyOtp",
  "googleLogin",
  "getTranslation",
];

const matchesEndpoints = (url: string, list: string[]): boolean => {
  return list.some((endpoint) => url.includes(endpoint));
};

const GetBearerToken = async (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Promise<HttpEvent<any>> => {
  const auth = inject(Auth);
  const authService = inject(AuthService);

  await auth.authStateReady();
  const token = await authService.getToken();

  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
        "x-api-key": environment.x_api_key,
      },
    });
  }
  return lastValueFrom(next(req));
};

const GetOnlyBearerToken = async (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Promise<HttpEvent<any>> => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const storage = inject(StorageService);

  await auth.authStateReady();
  const token = await authService.getToken();
  const tenantInfo = (await storage.get("tenantInfo")) as {
    connections?: { token?: string };
  } | null;
  const xApiKey = tenantInfo?.connections?.token ?? "";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = "Bearer " + token;
  if (xApiKey) headers["X-APIKey"] = xApiKey;

  req = req.clone({ setHeaders: headers });
  return lastValueFrom(next(req));
};

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH_INTERCEPTOR)) {
    return from(GetOnlyBearerToken(req, next));
  }

  if (matchesEndpoints(req.url, PUBLIC_ENDPOINTS)) {
    req = req.clone({
      setHeaders: {
        "x-api-key": environment.x_api_key,
        "Content-Type": "application/json",
      },
    });
    return next(req);
  } else if (matchesEndpoints(req.url, NO_HEADER_ENDPOINTS)) {
    return next(req);
  } else {
    const storage = inject(StorageService);
    const network = inject(NetworkService);
    if (matchesEndpoints(req.url, OFFLINE_ENDPOINTS)) {
      const isOnline = network.getCurrentNetworkStatus();
      if (!isOnline) {
        return from(
          (async () => {
            const requests = (await storage.get("requests")) || [];
            const body = req.body as {
              offlineId?: string;
              findingId?: string;
              id?: string;
              assetId?: string;
              assessmentId?: string;
            } | null;
            const entityId =
              body?.offlineId || body?.findingId || body?.id || body?.assessmentId;
            const existingIndex = entityId
              ? requests.findIndex(
                  (queued: { url: string; body?: any }) =>
                    queued.url === req.url &&
                    entityId &&
                    (queued.body?.offlineId === entityId ||
                      queued.body?.findingId === entityId ||
                      queued.body?.id === entityId ||
                      queued.body?.assessmentId === entityId) &&
                    (!body?.assetId || !queued.body?.assetId
                      ? true
                      : queued.body.assetId === body.assetId),
                )
              : -1;

            const queuedRequest = {
              url: req.url,
              body: req.body,
              method: req.method,
              headers: req.headers,
            };

            if (existingIndex !== -1) {
              requests[existingIndex] = queuedRequest;
            } else {
              requests.push(queuedRequest);
            }
            await storage.set("requests", requests);
            return new HttpResponse({ body: null, status: 200 });
          })(),
        );
      }
    }

    return from(GetBearerToken(req, next));
  }
};
